import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Textarea } from '@artosera/ui';
import styles from '../Ouvertures.module.css';
import { Champ, descriptionChamp, type PropsEtape } from './commun.js';

/**
 * Étape 5 : seulement ce que l'API applique. L'identité de l'émetteur des
 * factures vient de l'étape Galerie (on la relit ici sans la rouvrir) ; on
 * saisit les mentions complémentaires et le domaine propre. Ni numérotation
 * de factures, ni logo : la galerie les règle elle-même.
 */
export function EtapePrereglages({
  dossier,
  enCours,
  lectureSeule,
  sauver,
  allerA,
}: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const [mentions, setMentions] = useState(
    (dossier?.prereglages.mentionsComplementaires ?? []).join('\n'),
  );
  const [domaine, setDomaine] = useState(dossier?.prereglages.domaine ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  const identite = dossier?.identite;
  const adresse = identite?.adresse;
  const adresseTexte = adresse
    ? [
        adresse.ligne1,
        adresse.ligne2,
        `${adresse.codePostal} ${adresse.ville}`.trim(),
        adresse.pays,
      ]
        .filter((ligne) => ligne.trim() !== '')
        .join(', ')
    : '';
  const toutes: Array<[string, string]> = identite
    ? [
        [t('ouvertures.prereglages.raisonSociale'), identite.raisonSociale],
        [t('ouvertures.prereglages.forme'), identite.formeJuridique],
        [t('ouvertures.prereglages.siren'), identite.siren],
        [t('ouvertures.prereglages.siret'), identite.siret],
        [t('ouvertures.prereglages.tva'), identite.tvaIntracom],
        [t('ouvertures.prereglages.adresse'), adresseTexte],
      ]
    : [];
  const lignesEmetteur = toutes.filter(([, valeur]) => valeur.trim() !== '');

  const surEnvoi = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const lignes = mentions
      .split('\n')
      .map((ligne) => ligne.trim())
      .filter((ligne) => ligne !== '');
    if (lignes.length > 20 || lignes.some((ligne) => ligne.length > 500)) {
      setErreur(t('ouvertures.prereglages.erreurMentions'));
      return;
    }
    setErreur(null);
    void sauver(
      {
        prereglages: {
          mentionsComplementaires: lignes,
          domaine: domaine.trim() === '' ? null : domaine.trim().toLowerCase(),
        },
      },
      6,
    );
  };

  return (
    <form className={styles.etape} onSubmit={surEnvoi} noValidate>
      <header>
        <h2>{t('ouvertures.prereglages.titre')}</h2>
        <p>{t('ouvertures.prereglages.texte')}</p>
      </header>

      <fieldset className={styles.groupe} disabled={lectureSeule}>
        <h3>{t('ouvertures.prereglages.emetteur')}</h3>
        <p className={styles.groupeTexte}>{t('ouvertures.prereglages.emetteurTexte')}</p>
        {lignesEmetteur.length === 0 ? (
          <p className={styles.info}>{t('ouvertures.prereglages.emetteurVide')}</p>
        ) : (
          <dl className={styles.faits}>
            {lignesEmetteur.map(([cle, valeur]) => (
              <div key={cle} className={styles.faitsLigne}>
                <dt>{cle}</dt>
                <dd>{valeur}</dd>
              </div>
            ))}
          </dl>
        )}
        <div>
          <Button variant="ghost" onClick={() => allerA(1)}>
            {t('ouvertures.prereglages.modifierGalerie')}
          </Button>
        </div>

        <hr className={styles.separateur} />

        <Champ
          id="ouv-mentions"
          label={t('ouvertures.prereglages.mentions')}
          aide={t('ouvertures.prereglages.mentionsAide')}
          erreur={erreur ?? undefined}
        >
          <Textarea
            id="ouv-mentions"
            rows={5}
            value={mentions}
            onChange={(evenement) => setMentions(evenement.target.value)}
            {...descriptionChamp('ouv-mentions', { aide: true, erreur: erreur !== null })}
          />
        </Champ>

        <Champ
          id="ouv-domaine"
          label={t('ouvertures.prereglages.domaine')}
          aide={t('ouvertures.prereglages.domaineAide')}
        >
          <Input
            id="ouv-domaine"
            mono
            inputMode="url"
            autoCapitalize="none"
            value={domaine}
            onChange={(evenement) => setDomaine(evenement.target.value)}
            {...descriptionChamp('ouv-domaine', { aide: true })}
          />
        </Champ>

        <p className={styles.info}>{t('ouvertures.prereglages.note')}</p>
      </fieldset>

      <div className={styles.actionsEtape}>
        <Button variant="ghost" onClick={() => allerA(dossier?.identite.estTest ? 1 : 4)}>
          {t('ouvertures.assistant.precedent')}
        </Button>
        <div>
          <Button type="submit" loading={enCours} disabled={lectureSeule}>
            {enCours
              ? t('ouvertures.assistant.enregistrement')
              : t('ouvertures.assistant.continuer')}
          </Button>
        </div>
      </div>
    </form>
  );
}
